/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { CampaignStats, StudioChannel } from "./types";
import { STUDIO_CHANNELS } from "./types";
import { CHANNEL_META, formatMoney } from "./shared";

const pct = (n: number, d: number) => (d ? `${Math.round((n / d) * 100)}%` : "—");

// Funnel per channel plus the outcome numbers that matter: redemptions and revenue.
export const CampaignPerformance = defineComponent<{ stats: CampaignStats }>({
  name: "CampaignPerformance",
  props: ["stats"],
  setup(props) {
    const { t } = useLocalization();

    const Kpi = (label: string, value: string, sub?: string) => (
      <div class="rounded-xl bg-base-100 p-3">
        <div class="text-xs text-base-content/60">{label}</div>
        <div class="text-xl font-bold tabular-nums">{value}</div>
        {sub && <div class="text-xs text-base-content/50">{sub}</div>}
      </div>
    );

    return () => {
      const s = props.stats;
      const channels = STUDIO_CHANNELS.filter((c) => s.by_channel[c]) as StudioChannel[];
      return (
        <div class="space-y-4 bg-base-200 p-4">
          <div class="grid grid-cols-2 gap-2 md:grid-cols-5">
            {Kpi(t("campaigns.studio_list.delivered"), s.delivered.toLocaleString("de-CH"), pct(s.delivered, s.sent))}
            {Kpi(t("campaigns.studio_list.opened"), s.opened.toLocaleString("de-CH"), pct(s.opened, s.delivered))}
            {Kpi(t("campaigns.studio_list.clicked"), s.clicked.toLocaleString("de-CH"), pct(s.clicked, s.delivered))}
            {Kpi(t("campaigns.studio_list.redeemed"), s.redeemed.toLocaleString("de-CH"), pct(s.redeemed, s.delivered))}
            {Kpi(t("campaigns.studio_list.revenue"), formatMoney(s.revenue), t("campaigns.studio_list.unsubscribed", [s.unsubscribed]))}
          </div>
          <div class="overflow-x-auto rounded-xl bg-base-100">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>{t("campaigns.studio_list.channel")}</th>
                  <th class="text-right">{t("campaigns.studio_list.sent")}</th>
                  <th class="text-right">{t("campaigns.studio_list.delivered")}</th>
                  <th class="text-right">{t("campaigns.studio_list.opened")}</th>
                  <th class="text-right">{t("campaigns.studio_list.clicked")}</th>
                  <th class="w-1/3">{t("campaigns.studio_list.funnel")}</th>
                </tr>
              </thead>
              <tbody>
                {channels.map((ch) => {
                  const c = s.by_channel[ch]!;
                  return (
                    <tr key={ch}>
                      <td>
                        <span class="flex items-center gap-2">
                          <span class={["material-symbols-outlined text-base", CHANNEL_META[ch].tone]}>{CHANNEL_META[ch].icon}</span>
                          {t(CHANNEL_META[ch].label)}
                        </span>
                      </td>
                      <td class="text-right tabular-nums">{c.sent}</td>
                      <td class="text-right tabular-nums">{c.delivered}</td>
                      <td class="text-right tabular-nums">{ch === "sms" ? "—" : c.opened}</td>
                      <td class="text-right tabular-nums">{c.clicked}</td>
                      <td>
                        <div class="flex h-2 overflow-hidden rounded-full bg-base-200">
                          <div class="bg-primary" style={{ width: pct(c.clicked, c.sent) }} />
                          <div class="bg-primary/60" style={{ width: pct(Math.max(0, c.opened - c.clicked), c.sent) }} />
                          <div class="bg-primary/25" style={{ width: pct(c.delivered - Math.max(c.opened, c.clicked), c.sent) }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };
  },
});
